// Persisted dock preferences so Conductor survives Opendate's full-page
// navigations: whether it was open, docked-vs-modal, sidebar width, and the
// active thread. Keyed per team; localStorage is best-effort (private mode).

export const CONDUCTOR_WIDGET_WIDTH = 200;
export const CONDUCTOR_WIDGET_HEIGHT = 48;
export const ORB_SNAP_CAPTURE_RADIUS = 96;

const ORB_VIEWPORT_PADDING = 16;
const QUICK_CHAT_HEIGHT = 563;
const QUICK_CHAT_WIDTH = 425;

const storageKey = (teamId) => `venue_conductor_dock_${teamId || 'default'}`;
const orbStorageKey = (teamId, userId) =>
  `venue_conductor_orb_${teamId || 'default'}_${userId || 'default'}`;

const loadStoredState = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const saveStoredState = (key, patch) => {
  const next = { ...loadStoredState(key), ...patch };
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch (e) {
    // Persistence is a nice-to-have; never break the UI over storage.
  }
  return next;
};

export const DOCK_DEFAULT_WIDTH = 456;
export const DOCK_MIN_WIDTH = 360;
const DOCK_MIN_PAGE_WIDTH = 480;

export function clampDockWidth(width, viewportWidth) {
  const maxWidth = Math.max(DOCK_MIN_WIDTH, viewportWidth - DOCK_MIN_PAGE_WIDTH);
  return Math.round(Math.min(Math.max(width || DOCK_DEFAULT_WIDTH, DOCK_MIN_WIDTH), maxWidth));
}

export function loadDockState(teamId) {
  return loadStoredState(storageKey(teamId));
}

export function saveDockState(teamId, patch) {
  return saveStoredState(storageKey(teamId), patch);
}

export function getDefaultOrbPosition(
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0
) {
  return {
    x: Math.max(ORB_VIEWPORT_PADDING, viewportWidth - CONDUCTOR_WIDGET_WIDTH - ORB_VIEWPORT_PADDING),
    y: Math.max(
      ORB_VIEWPORT_PADDING,
      viewportHeight - CONDUCTOR_WIDGET_HEIGHT - ORB_VIEWPORT_PADDING - bottomInset
    )
  };
}

export function clampOrbPosition(
  position,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0
) {
  const fallback = getDefaultOrbPosition(viewportWidth, viewportHeight, bottomInset);
  const requestedX = Number(position?.x);
  const requestedY = Number(position?.y);
  const x = Number.isFinite(requestedX) ? requestedX : fallback.x;
  const y = Number.isFinite(requestedY) ? requestedY : fallback.y;
  const maxX = Math.max(ORB_VIEWPORT_PADDING, viewportWidth - CONDUCTOR_WIDGET_WIDTH - ORB_VIEWPORT_PADDING);
  const maxY = Math.max(
    ORB_VIEWPORT_PADDING,
    viewportHeight - CONDUCTOR_WIDGET_HEIGHT - ORB_VIEWPORT_PADDING - bottomInset
  );

  return {
    x: Math.round(Math.min(Math.max(x, ORB_VIEWPORT_PADDING), maxX)),
    y: Math.round(Math.min(Math.max(y, ORB_VIEWPORT_PADDING), maxY))
  };
}

export function getOrbSnapPoints(
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0
) {
  const minimumX = ORB_VIEWPORT_PADDING;
  const maximumX = Math.max(
    minimumX,
    viewportWidth - CONDUCTOR_WIDGET_WIDTH - ORB_VIEWPORT_PADDING
  );
  const minimumY = ORB_VIEWPORT_PADDING;
  const maximumY = Math.max(
    minimumY,
    viewportHeight - CONDUCTOR_WIDGET_HEIGHT - ORB_VIEWPORT_PADDING - bottomInset
  );
  const centerX = Math.round((minimumX + maximumX) / 2);
  const centerY = Math.round((minimumY + maximumY) / 2);

  return [
    { id: 'top-left', x: minimumX, y: minimumY },
    { id: 'top-center', x: centerX, y: minimumY },
    { id: 'top-right', x: maximumX, y: minimumY },
    { id: 'center-left', x: minimumX, y: centerY },
    { id: 'center-right', x: maximumX, y: centerY },
    { id: 'bottom-left', x: minimumX, y: maximumY },
    { id: 'bottom-center', x: centerX, y: maximumY },
    { id: 'bottom-right', x: maximumX, y: maximumY }
  ];
}

export function getOrbSnapCandidate(
  position,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0,
  captureRadius = ORB_SNAP_CAPTURE_RADIUS
) {
  const clamped = clampOrbPosition(position, viewportWidth, viewportHeight, bottomInset);
  const nearest = getOrbSnapPoints(viewportWidth, viewportHeight, bottomInset)
    .map((point) => ({
      ...point,
      distance: Math.hypot(point.x - clamped.x, point.y - clamped.y)
    }))
    .sort((firstPoint, secondPoint) => firstPoint.distance - secondPoint.distance)[0];

  return nearest?.distance <= captureRadius ? nearest : null;
}

export function getOrbPlacement(
  position,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0,
  snapPointId = null
) {
  const clamped = clampOrbPosition(position, viewportWidth, viewportHeight, bottomInset);
  if (snapPointId) return { snapPointId };

  const horizontalEdge = clamped.x + (CONDUCTOR_WIDGET_WIDTH / 2) <= viewportWidth / 2
    ? 'left'
    : 'right';
  const availableHeight = viewportHeight - bottomInset;
  const verticalEdge = clamped.y + (CONDUCTOR_WIDGET_HEIGHT / 2) <= availableHeight / 2
    ? 'top'
    : 'bottom';

  return {
    snapPointId: null,
    horizontalEdge,
    verticalEdge,
    horizontalInset: Math.round(horizontalEdge === 'left'
      ? clamped.x
      : viewportWidth - clamped.x - CONDUCTOR_WIDGET_WIDTH),
    verticalInset: Math.round(verticalEdge === 'top'
      ? clamped.y
      : availableHeight - clamped.y - CONDUCTOR_WIDGET_HEIGHT)
  };
}

export function restoreOrbPosition(
  position,
  placement,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  bottomInset = 0
) {
  const snapPoint = placement?.snapPointId
    ? getOrbSnapPoints(viewportWidth, viewportHeight, bottomInset)
      .find((point) => point.id === placement.snapPointId)
    : null;
  if (snapPoint) return { x: snapPoint.x, y: snapPoint.y };

  const horizontalInset = Number(placement?.horizontalInset);
  const verticalInset = Number(placement?.verticalInset);
  const hasHorizontalPlacement = Number.isFinite(horizontalInset)
    && ['left', 'right'].includes(placement?.horizontalEdge);
  const hasVerticalPlacement = Number.isFinite(verticalInset)
    && ['top', 'bottom'].includes(placement?.verticalEdge);
  if (!hasHorizontalPlacement || !hasVerticalPlacement) {
    return clampOrbPosition(position, viewportWidth, viewportHeight, bottomInset);
  }

  return clampOrbPosition({
    x: placement.horizontalEdge === 'left'
      ? horizontalInset
      : viewportWidth - CONDUCTOR_WIDGET_WIDTH - horizontalInset,
    y: placement.verticalEdge === 'top'
      ? verticalInset
      : viewportHeight - bottomInset - CONDUCTOR_WIDGET_HEIGHT - verticalInset
  }, viewportWidth, viewportHeight, bottomInset);
}

export function getQuickChatFrame(
  position,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight
) {
  const width = Math.min(QUICK_CHAT_WIDTH, Math.max(0, viewportWidth - (ORB_VIEWPORT_PADDING * 2)));
  const height = Math.min(QUICK_CHAT_HEIGHT, Math.max(0, viewportHeight - (ORB_VIEWPORT_PADDING * 2)));
  const widgetX = Number(position?.x) || ORB_VIEWPORT_PADDING;
  const widgetY = Number(position?.y) || ORB_VIEWPORT_PADDING;
  const horizontalEdge = widgetX + (CONDUCTOR_WIDGET_WIDTH / 2) <= viewportWidth / 2 ? 'left' : 'right';
  const verticalEdge = widgetY + (CONDUCTOR_WIDGET_HEIGHT / 2) <= viewportHeight / 2 ? 'top' : 'bottom';
  const preferredLeft = horizontalEdge === 'left' ? widgetX : widgetX + CONDUCTOR_WIDGET_WIDTH - width;
  const preferredTop = verticalEdge === 'top' ? widgetY : widgetY + CONDUCTOR_WIDGET_HEIGHT - height;
  const left = Math.min(Math.max(preferredLeft, ORB_VIEWPORT_PADDING), viewportWidth - width - ORB_VIEWPORT_PADDING);
  const top = Math.min(Math.max(preferredTop, ORB_VIEWPORT_PADDING), viewportHeight - height - ORB_VIEWPORT_PADDING);

  return {
    left: Math.round(left),
    top: Math.round(top),
    width: Math.round(width),
    height: Math.round(height),
    placement: `${verticalEdge} ${horizontalEdge}`
  };
}

export function loadOrbState(teamId, userId) {
  return loadStoredState(orbStorageKey(teamId, userId));
}

export function saveOrbState(teamId, userId, patch) {
  return saveStoredState(orbStorageKey(teamId, userId), patch);
}
